import React, { Suspense, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import { cssMap } from '@atlaskit/css';
import { useResizingHeight } from '@atlaskit/motion';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { MinifiedAudioPlayer } from '../audio-player/MinifiedAudioPlayer';

import { PlaylistEmptyView } from './PlaylistEmptyView';
import { PlaylistErrorView } from './PlaylistErrorView';
import { PlaylistHeader } from './PlaylistHeader';
import { type PlaylistItemData } from './PlaylistItem';
import { PlaylistLoadingView } from './PlaylistLoadingView';
import { PlaylistView } from './PlaylistView';
import { usePlaylist } from './usePlaylist';

const styles = cssMap({
	wrapper: {
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface'),
		boxShadow: token('elevation.shadow.overflow'),
		position: 'absolute',
		bottom: token('space.100'),
		left: token('space.100'),
		right: token('space.100'),
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},
	contentContainer: {
		height: '400px',
		display: 'flex',
		flexDirection: 'column',
		overflowY: 'auto',
		position: 'relative',
	},
	coverBorder: {
		position: 'absolute',
		width: '100%',
		top: '0',
		backgroundColor: token('elevation.surface'),
		zIndex: 200,
		height: '12px',
	},
	stickyShadow: {
		position: 'sticky',
		top: token('space.negative.050'),
		zIndex: 1,
		boxShadow: token('elevation.shadow.overflow'),
		backgroundColor: token('elevation.surface'),
		height: '5px',
	},
	minifiedAudioPlayer: {
		marginLeft: token('space.100'),
		marginRight: token('space.100'),
		marginTop: token('space.100'),
		marginBottom: token('space.100'),
	},
});

type PlaylistWrapperProps = {
	children: React.ReactNode;
	currentItem: PlaylistItemData | null;
	defaultExpanded?: boolean;
	hasOtherActivePlayback?: boolean;
};

const PlaylistWrapper = ({
	children,
	currentItem,
	defaultExpanded = false,
	hasOtherActivePlayback = false,
}: PlaylistWrapperProps) => {
	const resizingHeightProps = useResizingHeight();
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);

	const handleToggle = () => setIsExpanded((prev) => !prev);

	return (
		<Box xcss={styles.wrapper}>
			<PlaylistHeader isExpanded={isExpanded} onToggle={handleToggle} />
			{!isExpanded && hasOtherActivePlayback && currentItem && (
				<Box xcss={styles.minifiedAudioPlayer}>
					<MinifiedAudioPlayer
						title={currentItem?.title || ''}
						byline=""
						avatarUrl={''}
						isPlaying
						elevated
					/>
				</Box>
			)}
			<div {...resizingHeightProps}>
				{isExpanded && (
					<Flex xcss={styles.contentContainer}>
						{/* Scroll shadow effect */}
						<Box xcss={styles.coverBorder} />
						<Box>
							<Box xcss={styles.stickyShadow} />
							{children}
						</Box>
					</Flex>
				)}
			</div>
		</Box>
	);
};

type PlaylistInnerProps = {
	currentItem: PlaylistItemData | null;
	setCurrentItem: (item: PlaylistItemData | null) => void;
};

const PlaylistInner = ({ currentItem, setCurrentItem }: PlaylistInnerProps) => {
	const { playlistItems } = usePlaylist();

	const handleItemClick = (item: PlaylistItemData) => {
		setCurrentItem(item);
	};

	if (playlistItems.length === 0) {
		return <PlaylistEmptyView />;
	}

	return (
		<>
			{playlistItems.length === 0 ? (
				<PlaylistEmptyView />
			) : (
				<PlaylistView
					items={playlistItems}
					currentItem={currentItem}
					onItemClick={handleItemClick}
				/>
			)}
		</>
	);
};

type PlaylistProps = {
	defaultExpanded?: boolean;
	hasOtherActivePlayback?: boolean;
};

export const Playlist = ({ defaultExpanded, hasOtherActivePlayback }: PlaylistProps) => {
	const [currentItem, setCurrentItem] = useState<PlaylistItemData | null>(null);

	return (
		<PlaylistWrapper
			currentItem={currentItem}
			defaultExpanded={defaultExpanded}
			hasOtherActivePlayback={hasOtherActivePlayback}
		>
			<ErrorBoundary
				fallbackRender={(props) => <PlaylistErrorView {...props} onRetry={() => {}} />}
			>
				<Suspense fallback={<PlaylistLoadingView />}>
					<PlaylistInner currentItem={currentItem} setCurrentItem={setCurrentItem} />
				</Suspense>
			</ErrorBoundary>
		</PlaylistWrapper>
	);
};
